import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { InputText } from "../../../components/InputText";
import { Label } from "../../../components/ui/label";
import { PerfilModal } from "../../../components/modals";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Users,
  Shield,
  CheckCircle,
  XCircle,
  Loader2,
  UserCheck,
  TrendingUp,
  AlertTriangle,
  Award,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import { api } from "../../../services/api";

interface Perfil {
  id: string;
  perfil: string;
  criado_em: string;
  alterado_em: string;
  descricao?: string;
  permissoes?: string[];
  patentes_ids?: (number | string)[]; // Aceita tanto number quanto UUID string
  status?: string;
}

export function Perfis() {
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [filteredPerfis, setFilteredPerfis] = useState<Perfil[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPerfil, setEditingPerfil] = useState<Perfil | null>(null);
  const [expandedPerfis, setExpandedPerfis] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadPerfis();
  }, []);



  const loadPerfis = async () => {
    try {
      setLoading(true);

      const response = await api.get("/perfis");
      const perfisComPermissoes = await Promise.all(
        response.data.map(async (perfil: Perfil) => {
          const [permissoes, patentes_ids] = await Promise.all([
            api.get(`/perfis/${perfil.id}/permissoes`)
              .then((r) => r.data || [])
              .catch(() => []),
            api.get(`/perfis/${perfil.id}/patentes`)
              .then((r) => r.data || [])
              .catch(() => []),
          ]);
          return { ...perfil, permissoes, patentes_ids };
        })
      );

      setPerfis(perfisComPermissoes);
      setFilteredPerfis(perfisComPermissoes);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar perfis", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Filtrar perfis baseado na busca
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      setFilteredPerfis(perfis);
    } else {
      const filtered = perfis.filter(
        (perfil) =>
          perfil.perfil.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (perfil.descricao && perfil.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPerfis(filtered);
    }
  };

  // Atualizar filtro quando o termo de busca mudar
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPerfis(perfis);
    } else {
      const filtered = perfis.filter(
        (perfil) =>
          perfil.perfil.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (perfil.descricao && perfil.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPerfis(filtered);
    }
  }, [searchTerm, perfis]);







  const handleEdit = (perfil: Perfil) => {
    setEditingPerfil(perfil);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPerfil(null);
  };

  const handleSaveSuccess = () => {
    loadPerfis();
    handleCloseModal();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Tem certeza que deseja excluir este perfil?")) {
      try {
        // Como não temos DELETE na API, vamos simular
        enqueueSnackbar(
          `Funcionalidade de exclusão será implementada quando a API DELETE estiver disponível (ID: ${id})`,
          {
            variant: "info",
          },
        );
      } catch (error: any) {
        enqueueSnackbar(
          error.response?.data?.message || "Erro ao excluir perfil",
          { variant: "error" }
        );
      }
    }
  };

  const getPerfilIcon = (perfil: string) => {
    switch (perfil.toLowerCase()) {
      case 'administrador':
        return Shield;
      case 'oficial':
        return UserCheck;
      default:
        return Users;
    }
  };

  const getPerfilColor = (perfil: string) => {
    switch (perfil.toLowerCase()) {
      case 'administrador':
        return 'from-red-100 to-red-200 text-red-600';
      case 'oficial':
        return 'from-blue-100 to-blue-200 text-blue-600';
      default:
        return 'from-green-100 to-green-200 text-green-600';
    }
  };

  const toggleExpandPerfil = (perfilId: string) => {
    setExpandedPerfis(prev => {
      const newSet = new Set(prev);
      if (newSet.has(perfilId)) {
        newSet.delete(perfilId);
      } else {
        newSet.add(perfilId);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Users className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Perfis
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie os perfis de acesso e permissões do sistema
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conteúdo Principal */}
        <div className="lg:col-span-2 space-y-6">

          {/* Filtros de Busca */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                <Search className="w-5 h-5" />
                Buscar Perfis
              </CardTitle>
              <CardDescription>
                Digite o nome do perfil ou descrição para pesquisar ou deixe em branco para ver todos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="search">Nome do Perfil</Label>
                  <InputText
                    id="search"
                    name="search"
                    title=""
                    placeholder="Digite o nome do perfil..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Perfil
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
              <CardDescription>
                {filteredPerfis.length} perfil(s) encontrado(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : filteredPerfis.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum perfil encontrado
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredPerfis.map((perfil) => {
                    const IconComponent = getPerfilIcon(perfil.perfil);
                    return (
                      <div
                        key={perfil.id}
                        className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[oklch(0.45_0.15_142)]/10 rounded-lg flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-[oklch(0.45_0.15_142)]" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">{perfil.perfil}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              {perfil.permissoes && (
                                <Badge
                                  className="bg-blue-100 text-blue-800 border-blue-200 cursor-pointer hover:bg-blue-200 hover:text-blue-900 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700 dark:hover:bg-blue-900/60 dark:hover:text-blue-200 transition-colors"
                                  onClick={() => toggleExpandPerfil(perfil.id)}
                                >
                                  <Shield className="w-3 h-3 mr-1" />
                                  {perfil.permissoes.length} permissão(ões)
                                  {expandedPerfis.has(perfil.id) ? (
                                    <ChevronDown className="w-3 h-3 ml-1" />
                                  ) : (
                                    <ChevronRight className="w-3 h-3 ml-1" />
                                  )}
                                </Badge>
                              )}
                              {perfil.patentes_ids && perfil.patentes_ids.length > 0 ? (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700">
                                  <Award className="w-3 h-3 mr-1" />
                                  {perfil.patentes_ids.length} patente(s) automática(s)
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800/40 dark:text-gray-400 dark:border-gray-600">
                                  <Award className="w-3 h-3 mr-1" />
                                  Atribuição manual
                                </Badge>
                              )}
                            </div>
                            {perfil.descricao && (
                              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                                {perfil.descricao}
                              </p>
                            )}
                            {perfil.permissoes && perfil.permissoes.length > 0 && (
                              <div className="mt-2">
                                <div className="flex flex-wrap gap-1">
                                  {expandedPerfis.has(perfil.id)
                                    ? perfil.permissoes.map((permissao, index) => (
                                        <Badge
                                          key={index}
                                          className="bg-[oklch(0.45_0.15_142)]/10 text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 dark:bg-[oklch(0.45_0.15_142)]/20 dark:text-[oklch(0.75_0.15_142)] text-xs"
                                        >
                                          {permissao.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                                        </Badge>
                                      ))
                                    : (
                                      <>
                                        {perfil.permissoes.slice(0, 3).map((permissao, index) => (
                                          <Badge
                                            key={index}
                                            className="bg-[oklch(0.45_0.15_142)]/10 text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 dark:bg-[oklch(0.45_0.15_142)]/20 dark:text-[oklch(0.75_0.15_142)] text-xs"
                                          >
                                            {permissao.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                                          </Badge>
                                        ))}
                                        {perfil.permissoes.length > 3 && (
                                          <Badge className="bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600 text-xs">
                                            +{perfil.permissoes.length - 3} mais
                                          </Badge>
                                        )}
                                      </>
                                    )
                                  }
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(perfil)}
                            className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10 dark:text-[oklch(0.75_0.15_142)] dark:border-[oklch(0.45_0.15_142)]/40 dark:hover:bg-[oklch(0.45_0.15_142)]/20"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(perfil.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/30"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Painel Lateral */}
        <div className="space-y-6">
          {/* Estatísticas */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Estatísticas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total de Perfis</span>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                    {perfis.length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Administradores</span>
                  <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700">
                    {perfis.filter(p => p.perfil.toLowerCase() === "administrador").length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Oficiais</span>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                    {perfis.filter(p => p.perfil.toLowerCase() === "oficial").length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Encontrados</span>
                  <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700">
                    {filteredPerfis.length}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informações */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Dicas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-amber-50 dark:bg-amber-950/40 rounded-lg border border-transparent dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <Shield className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-900 dark:text-amber-300">Controle de Acesso</p>
                    <p className="text-amber-700 dark:text-amber-400">
                      Perfis definem permissões e níveis de acesso
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-blue-50 dark:bg-blue-950/40 rounded-lg border border-transparent dark:border-blue-800">
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-300">Hierarquia</p>
                    <p className="text-blue-700 dark:text-blue-400">
                      Organize perfis seguindo estrutura militar
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal de Perfil */}
      <PerfilModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveSuccess}
        initialData={editingPerfil}
      />
    </div>
  );
}
